<template>
  <div class="bg-gray-50 px-4 py-3 sm:px-6 border-b border-gray-200">
    <div class="flex flex-wrap items-center justify-between gap-4 mb-4">
      <div class="relative w-full sm:w-auto flex-1 flex items-center gap-4 justify-between">
        <div class="relative flex-1 max-w-[500px]">
          <label for="search" class="sr-only">Search...</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
              </svg>
            </div>
            <input
              id="search"
              v-model="searchQuery"
              type="text"
              :placeholder="searchPlaceholder"
              class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#00225E] focus:border-[#00225E] sm:text-sm"
            />
          </div>
        </div>

        <button
          @click="exportToExcel"
          :disabled="exporting"
          class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00225E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="!exporting" class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
          {{ exporting ? 'Exporting...' : 'Export to Excel' }}
        </button>

        <button
          @click="toggleAdvancedFilters"
          class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00225E] transition-colors duration-200"
        >
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Advanced Filters
          <svg 
            class="h-4 w-4 ml-2 transition-transform duration-200" 
            :class="{ 'rotate-180': showAdvancedFilters }"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div v-if="userCompanies && userCompanies.length > 0" class="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div class="flex items-center">
          <svg class="h-5 w-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <div>
            <p class="text-sm font-medium text-blue-800">Filtered by Company</p>
            <p class="text-sm text-blue-600">{{ userCompanies.join(', ') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div 
      v-show="showAdvancedFilters" 
      class="transition-all duration-300 ease-in-out"
      :class="showAdvancedFilters ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0'"
    >
      <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div class="grid grid-cols-1 gap-4" :class="hideStatusFilter ? 'md:grid-cols-1' : 'md:grid-cols-3'">
          <div>
            <label class="block text-sm font-medium text-[#00225E] mb-1">Category</label>
            <select 
              v-model="filters.category" 
              class="w-full p-2 border border-gray-300 text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00225E] focus:border-[#00225E]"
              @change="applyFilters"
              :disabled="loadingCategories"
            >
              <option value="">{{ loadingCategories ? 'Loading categories...' : 'All Categories' }}</option>
              <option v-for="category in categories" :key="category" :value="category">{{ category }}</option>
            </select>
          </div>

          <div v-if="!hideStatusFilter">
            <label class="block text-sm font-medium text-[#00225E] mb-1">Status</label>
            <select 
              v-model="filters.status" 
              class="w-full p-2 border border-gray-300 text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00225E] focus:border-[#00225E]"
              @change="applyFilters"
              :disabled="loadingStatuses"
            >
              <option value="">{{ loadingStatuses ? 'Loading statuses...' : 'All Statuses' }}</option>
              <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
            </select>
          </div>

        </div>

        <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[#00225E] mb-1">From Date</label>
            <input
              v-model="filters.fromDate"
              type="date"
              class="w-full p-2 border border-gray-300 text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00225E] focus:border-[#00225E]"
              @change="applyFilters"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-[#00225E] mb-1">To Date</label>
            <input
              v-model="filters.toDate"
              type="date"
              class="w-full p-2 border border-gray-300 text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00225E] focus:border-[#00225E]"
              @change="applyFilters"
            />
          </div>
        </div>

        <div class="mt-4 flex justify-between items-center">
          <div class="text-sm text-gray-600">
            <span v-if="hasActiveFilters">
              Active filters: 
              <span v-if="filters.category" class="font-medium text-[#00225E]">Category: {{ filters.category }}</span>
              <span v-if="filters.woNumber" class="font-medium text-[#00225E]"> | WO#: {{ filters.woNumber }}</span>
              <span v-if="filters.datePeriod" class="font-medium text-[#00225E]"> | Period: {{ filters.datePeriod }}</span>
              <span v-if="filters.fromDate || filters.toDate" class="font-medium text-[#00225E]"> | Date Range</span>
            </span>
            <span v-else>No filters applied</span>
          </div>
          <button
            @click="clearFilters"
            class="text-sm text-[#00225E] hover:text-[#00225E]/80 font-medium"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useExcelExport } from "../composables/useExcelExport";

export default {
  props: {
    searchPlaceholder: {
      type: String,
      default: "Search..."
    },
    data: {
      type: Array,
      default: () => []
    },
    userCompanies: {
      type: Array,
      default: () => []
    },
    exportFilename: {
      type: String,
      default: "export"
    },
    isWorkOrder: {
      type: Boolean,
      default: false
    },
    hideStatusFilter: {
      type: Boolean,
      default: false
    },
    exportColumns: {
      type: Array,
      default: () => []
    },
    onExport: {
      type: Function,
      default: null
    }
  },
  emits: ['update:searchQuery', 'filter-change'],
  setup(props, { emit }) {
    const store = useStore();
    const { exportToExcel, formatDataForExport } = useExcelExport();
    const searchQuery = ref("");
    const showAdvancedFilters = ref(false);
    const allCategories = ref([]);
    const loadingCategories = ref(false);
    const allStatuses = ref([]);
    const loadingStatuses = ref(false);
    const exporting = ref(false);
    
    const filters = ref({
      category: "",
      status: "",
      woNumber: "",
      datePeriod: "",
      fromDate: "",
      toDate: ""
    });

    const loadCategories = async () => {
      loadingCategories.value = true;
      try {
        const response = await store.dispatch('getAllCategories');
        if (response.ok) {
          allCategories.value = response.data;
        } else {
          const unique = new Set();
          props.data.forEach(item => {
            if (item.category) unique.add(item.category);
          });
          allCategories.value = Array.from(unique).sort();
        }
      } catch (error) {
        const unique = new Set();
        props.data.forEach(item => {
          if (item.category) unique.add(item.category);
        });
        allCategories.value = Array.from(unique).sort();
      } finally {
        loadingCategories.value = false;
      }
    };

    const loadStatuses = async () => {
      loadingStatuses.value = true;
      try {
        // Use different endpoint based on whether it's Work Orders or Invoices
        const action = props.isWorkOrder ? 'getWorkOrderStatuses' : 'getAllStatuses';
        const response = await store.dispatch(action);
        if (response.ok) {
          allStatuses.value = response.data;
        } else {
          const statusSet = new Set();
          if (props.data && props.data.length > 0) {
            props.data.forEach(item => {
              if (item.status) {
                statusSet.add(item.status);
              }
            });
          }
          allStatuses.value = Array.from(statusSet).sort();
        }
      } catch (error) {
        const statusSet = new Set();
        if (props.data && props.data.length > 0) {
          props.data.forEach(item => {
            if (item.status) {
              statusSet.add(item.status);
            }
          });
        }
        allStatuses.value = Array.from(statusSet).sort();
      } finally {
        loadingStatuses.value = false;
      }
    };

    const categories = computed(() => {
      if (allCategories.value.length > 0) {
        return allCategories.value;
      }
      // Fallback to local data extraction
      const unique = new Set();
      props.data.forEach(item => {
        if (item.category) unique.add(item.category);
      });
      return Array.from(unique).sort();
    });

    const statuses = computed(() => {
      if (allStatuses.value.length > 0) {
        return allStatuses.value;
      }
      // Fallback to local data extraction
      const unique = new Set();
      if (props.data && props.data.length > 0) {
        props.data.forEach(item => {
          if (item.status) unique.add(item.status);
        });
      }
      return Array.from(unique).sort();
    });

    const workOrders = computed(() => {
      const unique = new Set();
      props.data.forEach(item => {
        if (item.woNumber) unique.add(item.woNumber);
      });
      return Array.from(unique).sort();
    });

    const datePeriods = computed(() => {
      const unique = new Set();
      props.data.forEach(item => {
        if (item.period) unique.add(item.period);
      });
      return Array.from(unique).sort();
    });

    const hasActiveFilters = computed(() => {
      return filters.value.category || 
             filters.value.status ||
             filters.value.woNumber || 
             filters.value.datePeriod || 
             filters.value.fromDate || 
             filters.value.toDate;
    });

    const toggleAdvancedFilters = () => {
      showAdvancedFilters.value = !showAdvancedFilters.value;
    };

    const applyFilters = () => {
      emit('filter-change', {
        searchQuery: searchQuery.value,
        ...filters.value
      });
    };

    const clearFilters = () => {
      filters.value = {
        category: "",
        status: "",
        woNumber: "",
        datePeriod: "",
        fromDate: "",
        toDate: ""
      };
      searchQuery.value = "";
      applyFilters();
    };

    const handleExportToExcel = async () => {
      if (exporting.value) return;
      
      exporting.value = true;
      
      try {
        if (props.onExport && typeof props.onExport === 'function') {
          const exportData = await props.onExport({
            searchQuery: searchQuery.value,
            ...filters.value
          });
          if (exportData && exportData.length > 0) {
            const formattedData = props.exportColumns.length > 0 
              ? formatDataForExport(exportData, props.exportColumns)
              : exportData;
            
            exportToExcel(formattedData, props.exportFilename);
          } else {
          }
        } else {
          if (props.data && props.data.length > 0) {
            const formattedData = props.exportColumns.length > 0 
              ? formatDataForExport(props.data, props.exportColumns)
              : props.data;
            
            exportToExcel(formattedData, props.exportFilename);
          } else {
          }
        }
      } catch (error) {
      } finally {
        exporting.value = false;
      }
    };

    watch(searchQuery, () => {
      emit('update:searchQuery', searchQuery.value);
      applyFilters();
    });

    watch(() => props.data, (newData) => {
      if (newData && newData.length > 0) {
        loadStatuses();
      }
    }, { deep: true, immediate: true });

    onMounted(() => {
      loadCategories();
      loadStatuses();
    });

    return {
      searchQuery,
      showAdvancedFilters,
      filters,
      categories,
      statuses,
      workOrders,
      datePeriods,
      hasActiveFilters,
      loadingStatuses,
      exporting,
      toggleAdvancedFilters,
      applyFilters,
      clearFilters,
      exportToExcel: handleExportToExcel
    };
  }
};
</script>
